// ============================================================================
// APP CONFIGURATION — TENANT-AWARE PRESET LOADER
// ============================================================================
// The active app config is resolved from VITE_THEME env var:
//   VITE_THEME=dumax       → src/config/apps/dumax-config.json
//   VITE_THEME=central-gps → src/config/apps/central-gps-config.json
//
// Fallback chain: {tenant}-config.json → default-config.json
//
// Usage:
//   import { appsConfig, isCategoryEnabled, isAppEnabled, isMenuItemEnabled } from "./config/appsConfig";
// ============================================================================

import type {
  AppsPreset,
  CategoryConfig,
  AppConfig,
  ConnectionType,
} from "./apps/types";

// ---------------------------------------------------------------------------
// Load all app config JSON files eagerly at build time
// ---------------------------------------------------------------------------
const configModules = import.meta.glob<{ default: AppsPreset }>(
  "./apps/*.json",
  { eager: true },
);

// ---------------------------------------------------------------------------
// Resolve active preset: {VITE_THEME}-config.json → default-config.json
// ---------------------------------------------------------------------------
const tenant = import.meta.env.VITE_THEME as string | undefined;

function resolveModule(key: string): AppsPreset | undefined {
  const mod = configModules[key];
  if (!mod) return undefined;
  return (mod.default ?? mod) as unknown as AppsPreset;
}

const activeAppsPreset: AppsPreset =
  (tenant ? resolveModule(`./apps/${tenant}-config.json`) : undefined) ??
  resolveModule("./apps/default-config.json")!;

// ---------------------------------------------------------------------------
// Exported config object
// ---------------------------------------------------------------------------
export const appsConfig: AppsPreset = activeAppsPreset;

// ---------------------------------------------------------------------------
// Helper: is an entire category visible?
// ---------------------------------------------------------------------------
export function isCategoryEnabled(categoryId: string): boolean {
  const cat: CategoryConfig | undefined =
    activeAppsPreset.categories[categoryId];
  return cat?.enabled ?? false;
}

// ---------------------------------------------------------------------------
// Helper: is a specific app visible inside its category?
// ---------------------------------------------------------------------------
export function isAppEnabled(categoryId: string, appId: string): boolean {
  const cat: CategoryConfig | undefined =
    activeAppsPreset.categories[categoryId];
  if (!cat?.enabled) return false;
  const app: AppConfig | undefined = cat.apps[appId];

  // In development, allow apps with isComingSoon to be enabled
  const isDevelopment = import.meta.env.DEV;
  if (isDevelopment) {
    return app?.enabled ?? false;
  }

  // In production, only enable if not isComingSoon
  return Boolean(app?.enabled && !app?.isComingSoon);
}

// ---------------------------------------------------------------------------
// Helper: full app config (enabled + beta + pro + menu)
// ---------------------------------------------------------------------------
export function getAppConfig(
  categoryId: string,
  appId: string,
): AppConfig | undefined {
  return activeAppsPreset.categories[categoryId]?.apps[appId];
}

// ---------------------------------------------------------------------------
// Helper: is a specific menu item visible inside an app?
// ---------------------------------------------------------------------------
export function isMenuItemEnabled(
  categoryId: string,
  appId: string,
  menuItemId: string,
): boolean {
  const app = getAppConfig(categoryId, appId);
  if (!app?.enabled) return false;
  return app.menu[menuItemId] ?? false;
}

// ---------------------------------------------------------------------------
// Helper: which real-time connection type should the frontend use?
// ---------------------------------------------------------------------------
export function getConnectionType(): ConnectionType {
  return activeAppsPreset.connectionType ?? "websocket";
}

export default appsConfig;
